import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import PostCard from "../components/PostCard";
import StoriesBar from "../components/StoriesBar";
import { Button } from "../components/ui/button";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/feed`);
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to load feed");
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/react`, {
        reaction_type: reactionType,
      });
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? response.data.post : p))
      );
    } catch (error) {
      toast.error("Failed to react");
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">Your Feed</h1>
            <Button
              data-testid="create-post-btn"
              onClick={() => navigate("/create")}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Post</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stories Bar */}
        <div className="mb-6">
          <StoriesBar onCreateStory={() => toast.info("Story creation coming soon!")} />
        </div>

        {/* Welcome message for new users */}
        {posts.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-3">
              Welcome to HappyBook!
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-8">
              Your feed is empty. Start by creating your first post and spreading some positivity!
            </p>
            <Button
              data-testid="empty-feed-create-btn"
              onClick={() => navigate("/create")}
              className="btn-primary"
            >
              Create Your First Post
            </Button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="bento-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card-happy animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-muted"></div>
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-muted rounded"></div>
                    <div className="h-3 w-20 bg-muted rounded"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 w-full bg-muted rounded"></div>
                  <div className="h-4 w-3/4 bg-muted rounded"></div>
                </div>
                <div className="flex gap-4">
                  <div className="h-8 w-16 bg-muted rounded-full"></div>
                  <div className="h-8 w-16 bg-muted rounded-full"></div>
                  <div className="h-8 w-16 bg-muted rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Posts grid */}
        {!loading && posts.length > 0 && (
          <div className="bento-grid" data-testid="feed-posts-grid">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={user?.id}
                onReaction={handleReaction}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>

      <Navigation />
    </div>
  );
}
